import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Popconfirm, Tooltip } from "antd";
import { useForm } from "antd/es/form/Form";
import { useContext, useEffect, useState } from "react";
import { HomeContext } from "../../containers/Home";
import { GetAntIcon } from "../../utils/ant_icons";
import Emitter from "../../utils/emitter";
import FormButtonSave from "../FormButtonSave";
import { copyWellProgrammeTemplate } from "../../services/api-server/well_programme_template";
import useUpdateAllData from "../../hooks/useUpdateAllData";

const CopyTemplateModal = (props: any) => {
	const [formRef] = useForm();
	const [open, setOpen] = useState(false);
	const context: any = useContext(HomeContext);

	const { updateAllData } = useUpdateAllData();

	const [formReset, setFormReset] = useState<boolean>(true);
	const [updated, setUpdated] = useState<boolean>(false);
	const [initialForm, setInitialForm]: any = useState<any>(null);
	const [cancelPopover, setCancelPopover] = useState<any>(false);

	const resetForm = (values: any = null, reset: any = true) => {
		if (formRef) {
			setFormReset(true);
			reset && formRef.resetFields();
			formRef.setFieldsValue(values ? { ...values } : null);
			setInitialForm(values);
		}
	};

	// To reset the form when the modal is open or closed
	useEffect(() => {
		const _data = { phase_data: props.data };
		if (open) {
			resetForm(_data);
		} else {
			resetForm();
		}
	}, [open, props.data]);

	const handleFormSave = (values: any) => {
		copyWellProgrammeTemplate(
			context.currentTenantKey,
			values,
			props.otherData.operation_code,
			props.user
		)
			.then((data: any) => {
				Emitter.emit("alert", {
					type: "success",
					message: "Template copied successfully",
					description: "",
					top: true,
					closeable: false,
					timeout: 3000,
				});
				updateAllData(data, "new", "well_programme_template");
			})
			.catch((error: any) => {
				if (error.status === 409) {
					Emitter.emit("alert", {
						type: "error",
						message: "Template with this name already exists.",
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				} else {
					Emitter.emit("alert", {
						type: "error",
						message: "Failed to copy template.",
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				}
			})
			.finally(() => {
				resetForm();
				setOpen(false);
			});
	};

	const handleFormCancel = () => {
		resetForm();
		setOpen(false);
	};

	return (
		<>
			<Tooltip title="Save project setup to use as a template for future projects.">
				<Button onClick={() => setOpen(true)}>
					<div
						style={{
							display: "flex",
							alignContent: "center",
							justifyContent: "center",
							gap: "5px",
						}}
					>
						{GetAntIcon("copy")}
						<span>Save as template</span>
					</div>
				</Button>
			</Tooltip>

			<Modal
				closable={false}
				width={"540px"}
				title={
					<>
						<span>Save as template</span>{" "}
					</>
				}
				open={open}
				centered
				destroyOnClose
				footer={() => (
					<>
						<Popconfirm
							icon={<ExclamationCircleOutlined style={{ color: "grey" }} />}
							title={
								<div style={{ maxWidth: "300px", padding: "10px" }}>
									<>
										You have unsaved changes. Discard them or continue editing?
									</>
								</div>
							}
							open={cancelPopover}
							placement="topRight"
							cancelButtonProps={{ ghost: true }}
							cancelText="Discard changes"
							onCancel={() => {
								setCancelPopover(false);
								setTimeout(() => {
									handleFormCancel();
								}, 300);
							}}
							okText="Continue editing"
							okType="default"
							onConfirm={() => {
								setCancelPopover(false);
							}}
						>
							<Button
								className="ant-btn-secondary"
								onClick={() => {
									if (formReset) {
										handleFormCancel();
									} else {
										setCancelPopover(true);
									}
								}}
							>
								Cancel
							</Button>
						</Popconfirm>
						<FormButtonSave
							form={formRef}
							handleFormSave={handleFormSave}
							formReset={formReset}
						></FormButtonSave>
					</>
				)}
			>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						padding: "0 15px",
					}}
				>
					<span style={{ color: "rgba(255,255,255,0.45)" }}>
						Assigned roles, start date, and duration will not be included in the
						template.
					</span>
					<Form
						onChange={() => {
							setFormReset(false);
						}}
						style={{ gap: "10px", display: "flex", flexDirection: "column" }}
						requiredMark={"optional"}
						form={formRef}
						layout="vertical"
					>
						<Form.Item
							label="Template name"
							name="name"
							rules={[{ required: true, message: "Template name is required" }]}
						>
							<Input />
						</Form.Item>
						<Form.Item label="Description" name="description">
							<Input.TextArea />
						</Form.Item>
					</Form>
				</div>
			</Modal>
		</>
	);
};

export default CopyTemplateModal;
